import { handleApiResponse, notify } from '@/functions';
import { useMutation } from '@tanstack/react-query';
import { useInvalidateQueries } from '@/hooks';
import { apiClient } from '@/components/api';
import routes from '@/lib/routes';
import type { ICoinPackages, IIncognitoPackages, IBoostPackages } from '@/interfaces/paymentPackage.interface';

type PackageType = 'coin' | 'incognito' | 'boost';

type PackagePayload<T extends PackageType> =
  T extends 'coin' ? ICoinPackages :
  T extends 'incognito' ? IIncognitoPackages :
  IBoostPackages;

const packageUrl: Record<PackageType, string> = {
  coin: routes.api.coinPackages,
  incognito: routes.api.incognitoPackages,
  boost: routes.api.boostPackages,
};

export const useRequestSetPaymentPackages = <T extends PackageType>(type: T, onSaveSuccess: () => void) => {
  const url = packageUrl[type];
  const { invalidate } = useInvalidateQueries();

  const { isPending, error, mutateAsync } = useMutation({
    mutationKey: [url, 'set'],
    mutationFn: (packages: PackagePayload<T>) =>
      apiClient
        .post<void>(url, packages)
        .then(handleApiResponse)
        .catch(err => {
          notify(err?.internalMessage, 'error');
          throw err;
        }),
    onSuccess: async () => {
      await invalidate(url);
      onSaveSuccess();
    },
  });

  return {
    isLoadingSetPackages: isPending,
    triggerSetPackages: (packages: PackagePayload<T>) => mutateAsync(packages),
    error,
  };
};
